/**
 * 🔍 ANALIZADOR DE LOGS Y REGISTROS POR ID
 * 
 * Utilidad para extraer, filtrar y analizar registros específicos por ID
 * desde consola del navegador o datos de la aplicación
 * 
 * @author Sistema de Análisis
 * @date 2025-07-18
 */

/**
 * Clase principal para análisis de logs y registros
 */
class LogAnalyzer {
  constructor() {
    this.registros = [];
    this.filtros = {};
    this.debug = true;
  }

  /**
   * 📊 Interceptar y capturar logs de consola automáticamente
   */
  iniciarCaptura() {
    // Guardar referencia original de console.log
    this.originalConsoleLog = console.log;
    this.originalConsoleError = console.error;
    this.originalConsoleWarn = console.warn;

    // Interceptar console.log
    console.log = (...args) => {
      this.capturarLog('log', args);
      this.originalConsoleLog.apply(console, args);
    };

    // Interceptar console.error
    console.error = (...args) => {
      this.capturarLog('error', args);
      this.originalConsoleError.apply(console, args);
    };

    // Interceptar console.warn
    console.warn = (...args) => {
      this.capturarLog('warn', args);
      this.originalConsoleWarn.apply(console, args);
    };

    console.log('🔍 LogAnalyzer: Captura de logs iniciada');
  }

  /**
   * 🛑 Detener captura de logs
   */
  detenerCaptura() {
    if (this.originalConsoleLog) {
      console.log = this.originalConsoleLog;
      console.error = this.originalConsoleError;
      console.warn = this.originalConsoleWarn;
    }
    console.log('🔍 LogAnalyzer: Captura de logs detenida');
  }

  /**
   * 📝 Capturar un log específico
   */
  capturarLog(tipo, args) {
    const timestamp = new Date().toISOString();
    const contenido = args.map(arg => 
      typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
    ).join(' ');

    this.registros.push({
      timestamp,
      tipo,
      contenido,
      argumentos: args
    });
  }

  /**
   * 🎯 Filtrar registros por ID específico
   */
  filtrarPorId(id) {
    const registrosFiltrados = this.registros.filter(registro => {
      return registro.contenido.includes(id) || 
             registro.argumentos.some(arg => 
               typeof arg === 'object' && JSON.stringify(arg).includes(id)
             );
    });

    console.log(`🔍 Encontrados ${registrosFiltrados.length} registros con ID: ${id}`);
    return registrosFiltrados;
  }

  /**
   * 📋 Analizar datos específicos por ID (para datos de la aplicación)
   */
  analizarDatosPorId(datos, id, campo = 'ID') {
    if (!Array.isArray(datos)) {
      console.error('❌ Los datos deben ser un array');
      return [];
    }

    const registrosFiltrados = datos.filter(registro => {
      if (typeof registro === 'object' && registro[campo]) {
        return String(registro[campo]) === String(id);
      }
      return false;
    });

    console.log(`🎯 Analizando datos por ${campo}: ${id}`);
    console.log(`📊 Encontrados ${registrosFiltrados.length} registros`);
    
    if (registrosFiltrados.length > 0) {
      console.log('📝 Muestra del primer registro:');
      console.log(registrosFiltrados[0]);
    }

    return registrosFiltrados;
  }

  /**
   * 📊 Generar estadísticas de registros filtrados
   */
  generarEstadisticas(registros) {
    const stats = {
      total: registros.length,
      porTipo: {},
      porFecha: {},
      eventos: {},
      ubicaciones: new Set(),
      rango_fechas: {
        inicio: null,
        fin: null
      }
    };

    registros.forEach(registro => {
      // Estadísticas por tipo
      const tipo = registro.tipo || registro.Evento || registro.nombreEvento || 'Desconocido';
      stats.porTipo[tipo] = (stats.porTipo[tipo] || 0) + 1;

      // Estadísticas por fecha
      const fecha = registro.timestamp || registro.FechaReporte || registro.fecha;
      if (fecha) {
        const fechaStr = new Date(fecha).toDateString();
        stats.porFecha[fechaStr] = (stats.porFecha[fechaStr] || 0) + 1;

        // Rango de fechas
        const fechaObj = new Date(fecha);
        if (!stats.rango_fechas.inicio || fechaObj < stats.rango_fechas.inicio) {
          stats.rango_fechas.inicio = fechaObj;
        }
        if (!stats.rango_fechas.fin || fechaObj > stats.rango_fechas.fin) {
          stats.rango_fechas.fin = fechaObj;
        }
      }

      // Eventos específicos
      if (registro.Evento) {
        stats.eventos[registro.Evento] = (stats.eventos[registro.Evento] || 0) + 1;
      }

      // Ubicaciones únicas
      if (registro.latitude && registro.longitude) {
        stats.ubicaciones.add(`${registro.latitude},${registro.longitude}`);
      }
    });

    stats.ubicaciones_unicas = stats.ubicaciones.size;
    delete stats.ubicaciones; // Convertir Set a número

    return stats;
  }

  /**
   * 💾 Exportar registros a JSON para análisis posterior
   */
  exportarAJSON(registros, nombreArchivo = 'registros_analizados') {
    const datos = {
      metadata: {
        timestamp: new Date().toISOString(),
        total_registros: registros.length,
        analizador_version: '1.0.0'
      },
      estadisticas: this.generarEstadisticas(registros),
      registros: registros
    };

    const dataStr = JSON.stringify(datos, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${nombreArchivo}_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    console.log(`📁 Archivo ${nombreArchivo}.json descargado`);
    URL.revokeObjectURL(url);
  }

  /**
   * 📋 Exportar registros a CSV
   */
  exportarACSV(registros, nombreArchivo = 'registros_analizados') {
    if (registros.length === 0) {
      console.warn('⚠️ No hay registros para exportar');
      return;
    }

    // Obtener todas las claves únicas
    const todasLasClaves = new Set();
    registros.forEach(registro => {
      Object.keys(registro).forEach(clave => todasLasClaves.add(clave));
    });

    const encabezados = Array.from(todasLasClaves);
    const csvContent = [
      encabezados.join(','),
      ...registros.map(registro => 
        encabezados.map(encabezado => {
          const valor = registro[encabezado];
          if (typeof valor === 'object') {
            return `"${JSON.stringify(valor).replace(/"/g, '""')}"`;
          }
          return `"${String(valor || '').replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    const dataBlob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${nombreArchivo}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    console.log(`📁 Archivo ${nombreArchivo}.csv descargado`);
    URL.revokeObjectURL(url);
  }

  /**
   * 🎛️ Analizar registros de un vehículo específico en tiempo real
   */
  analizarVehiculoEnTiempoReal(vehiculoId) {
    console.log(`🚗 Iniciando análisis en tiempo real para vehículo ID: ${vehiculoId}`);
    
    // Configurar interceptor específico para este vehículo
    const interceptor = (datos) => {
      if (datos && (datos.ID === vehiculoId || datos.VehiculoUid === vehiculoId)) {
        console.log(`🎯 [TIEMPO REAL] Nuevo registro para ${vehiculoId}:`, datos);
        this.registros.push({
          timestamp: new Date().toISOString(),
          tipo: 'tiempo_real',
          vehiculo_id: vehiculoId,
          datos: datos
        });
      }
    };

    return interceptor;
  }

  /**
   * 📊 Mostrar resumen rápido en consola
   */
  mostrarResumen(registros) {
    const stats = this.generarEstadisticas(registros);
    
    console.log('\n📊 ===== RESUMEN DE ANÁLISIS =====');
    console.log(`📈 Total de registros: ${stats.total}`);
    
    if (stats.rango_fechas.inicio && stats.rango_fechas.fin) {
      console.log(`📅 Rango de fechas: ${stats.rango_fechas.inicio.toLocaleString()} - ${stats.rango_fechas.fin.toLocaleString()}`);
    }
    
    console.log('\n🎯 Eventos por tipo:');
    Object.entries(stats.eventos).forEach(([evento, cantidad]) => {
      console.log(`   ${evento}: ${cantidad} eventos`);
    });
    
    console.log(`\n🗺️ Ubicaciones únicas: ${stats.ubicaciones_unicas}`);
    
    console.log('\n💡 Para exportar los datos:');
    console.log('   analyzer.exportarAJSON(registros, "mi_analisis")');
    console.log('   analyzer.exportarACSV(registros, "mi_analisis")');
    console.log('=====================================\n');
  }

  /**
   * 🧹 Limpiar registros capturados
   */
  limpiarRegistros() {
    this.registros = [];
    console.log('🧹 Registros limpiados');
  }
}

// Crear instancia global del analizador
window.logAnalyzer = new LogAnalyzer();

// Funciones de conveniencia globales
window.analizarPorId = (id) => {
  const registros = window.logAnalyzer.filtrarPorId(id);
  window.logAnalyzer.mostrarResumen(registros);
  return registros;
};

window.exportarRegistros = (registros, formato = 'json', nombre = 'analisis') => {
  if (formato === 'csv') {
    window.logAnalyzer.exportarACSV(registros, nombre);
  } else {
    window.logAnalyzer.exportarAJSON(registros, nombre);
  }
};

// Funciones específicas para el ID solicitado
window.analizarVehiculo869530045231426 = () => {
  console.log('🎯 Analizando registros específicos para ID: 869530045231426');
  const registros = window.analizarPorId('869530045231426');
  window.lastAnalysis = registros; // Guardar para uso posterior
  return registros;
};

// Exportar la clase para uso en módulos
export default LogAnalyzer;
export { LogAnalyzer };

//console.log('🔍 LogAnalyzer cargado. Usa estas funciones en la consola:');
//console.log('   analizarPorId("869530045231426") - Buscar registros por ID');
//console.log('   analizarVehiculo869530045231426() - Análisis específico del vehículo');
//console.log('   logAnalyzer.iniciarCaptura() - Iniciar captura automática');
//console.log('   exportarRegistros(registros, "json") - Exportar a JSON');
//console.log('   exportarRegistros(registros, "csv") - Exportar a CSV');
